import { ref, set } from "@nuxtjs/composition-api";
import { defineStore } from "pinia";

import useBrandProfileApi from "@/store/brands/-api";
import { BrandProfile, BrandProfileFeedMap, BrandProfileFeedMeta, ProfileSlugMap } from "@/store/brands/types";
import { OK } from "@/utils/enums/http-codes";
import logger, { logLevel } from "@/utils/logger";

interface BrandProfileContext {
  $api: ReturnType<typeof useBrandProfileApi>;
}

export const useBrandsStore = defineStore(
  "brands",
  () => {
    const profiles = ref<ModelMap<BrandProfile>>({});
    const profileSlugMap = ref<ProfileSlugMap>({});
    const profileAdvertisersMap = ref<ModelMap<string[]>>({});
    const feeds = ref<BrandProfileFeedMap>({});
    const counts = ref<ModelMap<number>>({});
    const followedBrands = ref<ModelMap<boolean>>({});
    const stashedBrandFollow = ref(process.client ? sessionStorage.getItem("follow-brand") : "");
    const fetchState = ref<{ pending: boolean; error: boolean }>({ pending: false, error: false });

    // Setters
    const _setProfile = (brand: BrandProfile, slug: string): void => {
      if (!brand?.id) {
        return;
      }
      set(profiles.value, brand.id, brand);
      set(profileSlugMap.value, slug, brand.id);
    };

    const _setBrandAdvertiserIds = (profileId: string, advertiserIds: string[] | undefined): void => {
      if (!profileId || !advertiserIds || advertiserIds.length <= 0) {
        return;
      }
      set(profileAdvertisersMap.value, profileId, advertiserIds);
    };

    const _setFeed = (profileId: string, ids: string[], meta: BrandProfileFeedMeta): void => {
      if (!profileId || !ids.length) {
        return;
      }
      const existingIds = feeds.value[profileId]?.ids;
      let mergedIds: string[];

      if (existingIds) {
        const seen = new Set(existingIds);
        const addedIds: string[] = [];
        for (const id of ids) {
          if (!seen.has(id)) {
            seen.add(id);
            addedIds.push(id);
          }
        }
        mergedIds = addedIds.length ? [...existingIds, ...addedIds] : existingIds;
      } else {
        mergedIds = Array.from(new Set(ids));
      }

      set(feeds.value, profileId, { ids: mergedIds, meta });
    };

    const _setCount = (profileId: string, count: number | undefined): void => {
      if (!profileId || count === undefined) {
        return;
      }
      set(counts.value, profileId, count);
    };

    const _setFollowedBrand = (profileId: string, isFollowing: boolean | undefined): void => {
      if (!profileId || isFollowing === undefined) {
        return;
      }
      set(followedBrands.value, profileId, isFollowing);
    };

    const _setFollowedBrands = (profileIds: string[]): void => {
      for (const id of profileIds) {
        _setFollowedBrand(id, true);
      }
    };

    const setFetchState = (pending: boolean, error: boolean): void => {
      fetchState.value.pending = pending;
      fetchState.value.error = error;
    };

    const getProfileBySlug = (slug: string) => {
      if (slug) {
        const profileId = profileSlugMap.value[slug];
        return profiles.value[profileId];
      }
    };

    const stashBrandFollow = (profileId: string): void => {
      stashedBrandFollow.value = profileId;
    };

    const haveShopperFollowsBeenFetched = (): boolean => {
      return Boolean(Object.keys(followedBrands.value).length);
    };

    const hasBrandProfileBeenFetched = (profileId: string): boolean => {
      return Boolean(profiles.value[profileId]?.id);
    };

    // Network
    async function fetchBrandProfile(this: BrandProfileContext, slug: string): Promise<BrandProfile | undefined> {
      if (!slug) {
        throw new Error("slug must be provided");
      }

      if (profileSlugMap.value[slug]) {
        return;
      }

      try {
        // this is the only endpoint that supports lookup by slug, so we have to return the
        // profileId if found, or null to signal if this should be a 404
        const response = await this.$api.getBrandProfile(slug);

        if (response.status !== OK) {
          return;
        }

        _setProfile(response.data, slug);

        return profiles.value[response.data.id];
      } catch (error) {
        logger(logLevel.ERROR, "Error during fetchBrandProfile()", { meta: { error, profileId: slug } });
      }
    }

    async function fetchBrandAdvertiserId(this: BrandProfileContext, profileId: string) {
      if (!profileId) {
        throw new Error("profileId must be supplied");
      }

      const accountId = profiles.value[profileId]?.accountId;
      const advertiser = profileAdvertisersMap.value[profileId];

      if (accountId === undefined || advertiser !== undefined) {
        return;
      }

      try {
        const response = await this.$api.getBrandAdvertiserId(accountId);

        if (response.status !== OK) {
          return;
        }

        const advertiserIds = response.data?.data?.brandAdvertisers.advertisers.map((item) => String(item.id));

        _setBrandAdvertiserIds(profileId, advertiserIds);
      } catch (error) {
        logger(logLevel.ERROR, "Error during fetchBrandAdvertiserId()", {
          meta: { error, profileId },
        });
      }
    }

    async function fetchBrandFeed(this: BrandProfileContext, profileId: string): Promise<string[] | undefined> {
      if (!profileId) {
        throw new Error("id must be provided");
      }

      const meta = feeds.value[profileId]?.meta;

      if (meta && (!meta?.hasMore || !meta?.nextCursor)) {
        return;
      }

      try {
        const response = await this.$api.getBrandFeed(profileId, meta);

        if (response?.status !== OK) {
          return;
        }

        const postIds = response.data?.posts?.map((item) => item.postId) || [];

        _setFeed(profileId, postIds, response.data?.pagination);

        return postIds;
      } catch (error) {
        logger(logLevel.ERROR, "Error during fetchBrandFeed()", { meta: { error, profileId } });
      }
    }

    async function fetchBrandFollowerCount(this: BrandProfileContext, profileId: string): Promise<void> {
      if (!profileId) {
        throw new Error("id must be provided");
      }

      try {
        const response = await this.$api.getBrandFollowerCount(profileId);

        if (response.status !== OK) {
          return;
        }

        _setCount(profileId, response.data?.count);
      } catch (error) {
        logger(logLevel.ERROR, "Error during fetchBrandFollowerCount()", { meta: { error, profileId } });
      }
    }

    async function fetchShopperBrandsFollowed(this: BrandProfileContext, sigilId: string) {
      if (!sigilId) {
        throw new Error("sigilId must be provided");
      }

      try {
        const response = await this.$api.getShopperBrandsFollowed(sigilId);

        if (response.status !== OK) {
          return;
        }

        _setFollowedBrands(response.data?.nodes?.map((item) => item.id) || []);
      } catch (error) {
        logger(logLevel.ERROR, "Error during fetchShopperBrandsFollowed()", { meta: { error, sigilId } });
      }
    }

    async function addBrandFollow(this: BrandProfileContext, profileId: string, sigilId: string) {
      if (!profileId || !sigilId) {
        throw new Error("id and sigilID must be provided");
      }

      try {
        const response = await this.$api.addShopperBrandFollow(profileId, sigilId);

        if (response.status !== OK) {
          return;
        }

        _setFollowedBrand(profileId, true);
      } catch (error) {
        logger(logLevel.ERROR, "Error during addBrandFollow()", { meta: { error, profileId, sigilId } });
      }
    }

    async function removeBrandFollow(this: BrandProfileContext, profileId: string): Promise<void> {
      if (!profileId) {
        throw new Error("id must be provided");
      }

      try {
        const response = await this.$api.removeShopperBrandFollow(profileId);

        if (response.status !== OK) {
          return;
        }

        _setFollowedBrand(profileId, false);
      } catch (error) {
        logger(logLevel.ERROR, "Error during removeBrandFollow()", { meta: { error, profileId } });
      }
    }

    return {
      profiles,
      profileSlugMap,
      profileAdvertisersMap,
      feeds,
      counts,
      followedBrands,
      stashedBrandFollow,
      fetchState,
      setFetchState,
      getProfileBySlug,
      stashBrandFollow,
      hasBrandProfileBeenFetched,
      haveShopperFollowsBeenFetched,
      fetchBrandProfile,
      fetchBrandAdvertiserId,
      fetchBrandFeed,
      fetchBrandFollowerCount,
      fetchShopperBrandsFollowed,
      addBrandFollow,
      removeBrandFollow,
    };
  },
  {
    api: useBrandProfileApi,
  },
);
